
      import QrScanner from 'https://cdn.jsdelivr.net/npm/qr-scanner@1.4.2/qr-scanner.min.js';
      import { QRTPC } from '@folkjs/labs/QRTP-C';

      // Make modules available globally for debugging
      window.QRTPC = QRTPC;
      window.QrScanner = QrScanner;

      // Core state
      let qrScanner = null;
      let cameraActive = false;
      let senderIterator = null;
      let qrtpc = new QRTPC();
      let currentMode = 'sender';

      // Scan rate tracking
      let scanSuccesses = 0;
      let scanHistory = []; // Array of {timestamp, successes}
      let scanRateInterval = null;
      const ASSUMED_SCAN_RATE = 25; // fps from maxScansPerSecond

      // Bitrate tracking
      let receptionStartTime = null;

      // Checksum tracking
      let senderChecksum = '';
      let receiverChecksum = '';

      // Initialize after DOM is loaded
      document.addEventListener('DOMContentLoaded', initApp);

      function initApp() {
        // Get DOM elements
        const elements = getElements();

        // Setup event handlers
        setupUIEvents(elements);

        // Initialize UI
        updateQRCode('', elements.qrcodeContainer);

        // Set initial message from textarea
        const initialText = elements.txData.value.trim();
        if (initialText) {
          startSending(initialText, elements);
        }
      }

      function getElements() {
        return {
          txData: document.getElementById('txData'),
          qrcodeContainer: document.getElementById('qrcode'),
          video: document.getElementById('video'),
          videoContainer: document.getElementById('videoContainer'),
          cameraOverlay: document.getElementById('cameraOverlay'),
          cameraLabel: document.getElementById('cameraOverlay').querySelector('.camera-label'),
          sendingProgressBar: document.getElementById('sendingProgressBar'),
          sendingProgressText: document.getElementById('sendingProgressText'),
          receivingProgressBar: document.getElementById('receivingProgressBar'),
          receivingProgressText: document.getElementById('receivingProgressText'),
          receivedTextDisplay: document.getElementById('receivedTextDisplay'),
          blockSize: document.getElementById('blockSize'),
          frameRate: document.getElementById('frameRate'),
          blockInfo: document.getElementById('blockInfo'),
          indicesDisplay: document.getElementById('indicesDisplay'),
          senderBtn: document.getElementById('senderBtn'),
          receiverBtn: document.getElementById('receiverBtn'),
          passthroughBtn: document.getElementById('passthroughBtn'),
          scanRateDisplay: document.getElementById('scanRateDisplay'),
        };
      }

      // Mode switching function
      function setMode(mode, elements) {
        if (currentMode === mode) return;

        currentMode = mode;

        // Update UI classes
        document.body.classList.remove('receiver-mode', 'passthrough-mode');
        if (mode === 'receiver') {
          document.body.classList.add('receiver-mode');
        } else if (mode === 'passthrough') {
          document.body.classList.add('passthrough-mode');
        }

        // Update button states
        elements.senderBtn.classList.toggle('active', mode === 'sender');
        elements.receiverBtn.classList.toggle('active', mode === 'receiver');
        elements.passthroughBtn.classList.toggle('active', mode === 'passthrough');

        // Reset protocol state
        qrtpc.reset();
        resetReceivingUI(elements);

        // Reset scan rate tracking
        stopScanRateTracking(elements);

        if (mode === 'sender') {
          // Stop camera if active
          if (cameraActive) {
            stopCamera(elements);
          }
          // Start sending if there's text
          const text = elements.txData.value.trim();
          if (text) {
            startSending(text, elements);
          }
        } else if (mode === 'receiver') {
          // receiver mode
          // Stop sending
          stopSending(elements);
          // Start camera if not active
          if (!cameraActive) {
            startCamera(elements);
          }
        } else if (mode === 'passthrough') {
          // passthrough mode - scan and display
          // Stop sending
          stopSending(elements);
          // Clear QR code display initially
          updateQRCode('', elements.qrcodeContainer);
          // Start camera if not active
          if (!cameraActive) {
            startCamera(elements);
          }
        }
      }

      function startScanRateTracking(elements) {
        // Reset tracking
        scanSuccesses = 0;
        scanHistory = [];

        // Update display every 500ms
        scanRateInterval = setInterval(() => updateScanRateDisplay(elements), 500);
      }

      function stopScanRateTracking(elements) {
        if (scanRateInterval) {
          clearInterval(scanRateInterval);
          scanRateInterval = null;
        }
        // Reset display
        elements.scanRateDisplay.textContent = 'Scan rate: 25.0 fps | Success rate: 0.0 fps (0%)';
      }

      function updateScanRateDisplay(elements) {
        const now = Date.now();
        const windowMs = 3000; // 3 second rolling window

        // Add current snapshot to history
        scanHistory.push({ timestamp: now, successes: scanSuccesses });

        // Remove old entries outside the window
        scanHistory = scanHistory.filter((entry) => now - entry.timestamp <= windowMs);

        if (scanHistory.length < 2) {
          elements.scanRateDisplay.textContent = 'Scan rate: 25.0 fps | Success rate: 0.0 fps (0%)';
          return;
        }

        // Calculate success rate over the window
        const oldest = scanHistory[0];
        const newest = scanHistory[scanHistory.length - 1];
        const timeSpanSec = (newest.timestamp - oldest.timestamp) / 1000;

        if (timeSpanSec <= 0) return;

        const successRate = (newest.successes - oldest.successes) / timeSpanSec;
        const successPercent = (successRate / ASSUMED_SCAN_RATE) * 100;

        elements.scanRateDisplay.textContent = `Scan rate: ${ASSUMED_SCAN_RATE.toFixed(1)} fps | Success rate: ${successRate.toFixed(1)} fps (${successPercent.toFixed(0)}%)`;
      }

      async function calculateChecksum(text) {
        const encoder = new TextEncoder();
        const data = encoder.encode(text);
        const hashBuffer = await crypto.subtle.digest('SHA-256', data);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
        return hashHex.substring(0, 8); // First 8 characters
      }

      // Setup UI event handlers
      function setupUIEvents(elements) {
        // Text input handler
        elements.txData.addEventListener('input', function () {
          const text = elements.txData.value.trim();
          elements.receivedTextDisplay.textContent = '';

          // Reset receiver state
          qrtpc.reset();
          resetReceivingUI(elements);

          // Only start sending if in sender mode
          if (currentMode === 'sender') {
            if (text) {
              startSending(text, elements);
            } else {
              stopSending(elements);
            }
          }
        });

        // Control change handlers
        elements.blockSize.addEventListener('change', function () {
          const text = elements.txData.value.trim();
          if (text && currentMode === 'sender') {
            startSending(text, elements);
          }
        });

        elements.frameRate.addEventListener('change', function () {
          const text = elements.txData.value.trim();
          if (text && currentMode === 'sender') {
            startSending(text, elements);
          }
        });

        // Camera toggle handler
        elements.videoContainer.addEventListener('click', function () {
          if (cameraActive) {
            stopCamera(elements);
          } else {
            startCamera(elements);
          }
        });

        // Mode switcher handlers
        elements.senderBtn.addEventListener('click', function () {
          setMode('sender', elements);
        });

        elements.receiverBtn.addEventListener('click', function () {
          setMode('receiver', elements);
        });

        elements.passthroughBtn.addEventListener('click', function () {
          setMode('passthrough', elements);
        });
      }

      // Sending functions
      async function startSending(data, elements) {
        stopSending(elements);

        const blockSize = parseInt(elements.blockSize.value);
        const frameRate = parseInt(elements.frameRate.value);

        try {
          senderIterator = qrtpc.send(data, { blockSize, frameRate });

          // Calculate checksum for sender
          senderChecksum = await calculateChecksum(data);

          elements.sendingProgressBar.style.width = '100%';
          elements.sendingProgressText.textContent = `Transmitting... ${senderChecksum}`;

          // Start the sending loop
          sendNextBlock(elements);
        } catch (error) {
          console.error('Failed to start sender:', error);
        }
      }

      async function sendNextBlock(elements) {
        if (!senderIterator) return;

        try {
          const result = await senderIterator.next();
          if (!result.done) {
            updateQRCode(result.value, elements.qrcodeContainer);

            // Schedule next block
            setTimeout(() => sendNextBlock(elements), 0);
          }
        } catch (error) {
          console.error('Sending error:', error);
        }
      }

      function stopSending(elements) {
        senderIterator = null;
        senderChecksum = '';

        updateQRCode('', elements.qrcodeContainer);
        elements.sendingProgressBar.style.width = '0%';
        elements.sendingProgressText.textContent = 'Idle';
      }

      // Receiving functions
      async function processQRCode(qrData, elements) {
        // Handle passthrough mode - just display the QR code
        if (currentMode === 'passthrough') {
          updateQRCode(qrData, elements.qrcodeContainer);
          return;
        }

        try {
          const progress = qrtpc.receive(qrData);

          // Track successful scan (QR data was valid and processable)
          scanSuccesses++;

          const progressPercent = Math.round((progress.received / progress.needed) * 100);

          // Track reception bitrate
          if (!receptionStartTime) {
            receptionStartTime = Date.now();
          }

          elements.receivingProgressBar.style.width = `${progressPercent}%`;

          if (progress.complete) {
            // Calculate receiver checksum
            receiverChecksum = await calculateChecksum(progress.data);
            const checksumMatch = senderChecksum && receiverChecksum === senderChecksum;
            const statusText = checksumMatch ? '✓' : senderChecksum ? '✗' : '';

            elements.receivingProgressBar.classList.add('complete');
            elements.receivingProgressText.textContent = `Done! ${receiverChecksum} ${statusText}`;
            elements.receivedTextDisplay.textContent = progress.data;
            elements.blockInfo.textContent = `Complete! ${progress.received} indices used`;
            elements.indicesDisplay.textContent = 'All blocks received successfully';
          } else {
            // Simple bitrate estimation
            const elapsedSeconds = (Date.now() - receptionStartTime) / 1000;
            const estimatedBytes = progress.data?.length || 0;
            const bitrate = elapsedSeconds > 0 ? Math.round((estimatedBytes * 8) / elapsedSeconds) : 0;
            const bitrateText = bitrate > 0 ? ` (${formatBitrate(bitrate)})` : '';

            elements.receivingProgressText.textContent = `${progress.received}/${progress.needed} indices (${progressPercent}%)${bitrateText}`;
            elements.blockInfo.textContent = `${progressPercent}% complete`;
            elements.indicesDisplay.textContent = `Received ${progress.received} of ${progress.needed} unique indices`;
          }
        } catch (error) {
          console.error('QR processing error:', error);
          // Don't increment scanSuccesses for failed processing
        }
      }

      function resetReceivingUI(elements) {
        elements.receivingProgressBar.style.width = '0%';
        elements.receivingProgressBar.classList.remove('complete');
        elements.receivingProgressText.textContent = 'Waiting for blocks...';
        elements.blockInfo.textContent = '';
        elements.indicesDisplay.textContent = '';

        // Reset reception tracking
        receptionStartTime = null;
        receiverChecksum = '';

        // Reset scan rate display
        elements.scanRateDisplay.textContent = 'Scan rate: 25.0 fps | Success rate: 0.0 fps (0%)';
      }

      // Camera control functions
      async function startCamera(elements) {
        try {
          const video = elements.video;

          // Initialize QR Scanner
          qrScanner = new QrScanner(
            video,
            (result) => {
              // Process detected QR code
              processQRCode(result.data, elements);
            },
            {
              returnDetailedScanResult: true,
              highlightScanRegion: true,
              highlightCodeOutline: true,
              maxScansPerSecond: 25,
              onDecodeError: (error) => {
                // Optional: log decode errors for debugging
                // console.log('QR Decode Error:', error);
              },
            },
          );

          await qrScanner.start();

          // Start scan rate tracking
          startScanRateTracking(elements);

          // Update UI
          cameraActive = true;
          elements.videoContainer.classList.add('camera-active');
          elements.cameraLabel.textContent = 'Stop Camera';
        } catch (error) {
          console.error('Camera error:', error);
        }
      }

      function stopCamera(elements) {
        if (qrScanner) {
          qrScanner.stop();
          qrScanner.destroy();
          qrScanner = null;
        }

        // Stop scan rate tracking
        stopScanRateTracking(elements);

        // Update UI
        cameraActive = false;
        elements.videoContainer.classList.remove('camera-active');
        elements.cameraLabel.textContent = 'Start Camera';
      }

      // UI update helper functions
      function updateQRCode(qrData, container) {
        // Clear previous QR code
        container.innerHTML = '';

        if (!qrData) {
          container.innerHTML =
            '<div style="display: flex; align-items: center; justify-content: center; height: 100%; color: #999;">No data to encode</div>';
          return;
        }

        // Create a new canvas element
        const canvas = document.createElement('canvas');
        container.appendChild(canvas);

        // Calculate container size to determine QR code size
        const containerWidth = container.clientWidth;
        const containerHeight = container.clientHeight;
        const qrSize = Math.min(containerWidth, containerHeight);

        // Use smaller margin for passthrough mode to maximize QR code size
        const margin = currentMode === 'passthrough' ? 1 : 2;

        QRCode.toCanvas(
          canvas,
          qrData,
          {
            width: qrSize,
            margin: margin,
            errorCorrectionLevel: 'L', // Low error correction for maximum data
          },
          function (error) {
            if (error) {
              console.error(error);
            }
          },
        );
      }

      function formatBitrate(bitsPerSecond) {
        if (bitsPerSecond >= 1000000) {
          return `${(bitsPerSecond / 1000000).toFixed(1)} Mbps`;
        } else if (bitsPerSecond >= 1000) {
          return `${(bitsPerSecond / 1000).toFixed(1)} Kbps`;
        } else {
          return `${bitsPerSecond} bps`;
        }
      }
    